import { Injectable } from '@angular/core';
import { Action, Selector, State, Store } from '@ngxs/store';
import _ from 'lodash';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import { LineStateEnum } from 'src/app/samwebDescription/samwin/LineStateEnum';
import { CallsService } from 'src/generated/swagger';
import { EnabledFeaturesState } from '../../session/enabled-features/enabled-features.state';
import { CompleteTransferAction } from '../client-phone/client-phone.actions';
import { CurrentDialTargetState } from '../current-dial-target/current-dial-target.state';
import { NextDialTargetState } from '../next-dial-target/next-dial-target.state';
import { SamwinLinesStateSelector } from '../samwin-lines/samwin-lines.selector';
import { IShortCallInfo } from '../samwin-lines/samwin-lines.state';
import { TransferCallAction } from './transfer.actions';

// tslint:disable-next-line:no-empty-interface
export interface TransferStateModel {}

@State<TransferStateModel>({
   name: 'transfer',
})
@Injectable()
export class TransferState {
   constructor(private store: Store, private callService: CallsService) {}

   @Selector([CurrentDialTargetState.currentDialTargetNumber])
   public static getCurrentTransferNumber(currentDialTargetNumber: string): string {
      return currentDialTargetNumber;
   }

   @Selector([
      NextDialTargetState.nextDialTargetNumber,
      SamwinLinesStateSelector.getCurrentCallShortInfo,
      SamwinLinesStateSelector.isCallInProgress,
      SamwinLinesStateSelector.getDestinationLineState,
   ])
   public static canTransferConnected(transferNumber: string, currentCallInfo: IShortCallInfo, isCallInProgress: boolean, destination: LineStateEnum): boolean {
      return (
         isCallInProgress &&
         (!_.isEmpty(transferNumber) || destination !== LineStateEnum.IDLE) &&
         !_.isNil(currentCallInfo) &&
         !_.isNil(currentCallInfo.callId) &&
         currentCallInfo.peerId !== transferNumber
      );
   }

   @Selector([
      NextDialTargetState.nextDialTargetNumber,
      EnabledFeaturesState.isFeatureEnabled(FeatureEnum.ALLOW_TRANSFERRING_CALLS_BEFORE_ANSWERING),
      SamwinLinesStateSelector.getRingingCallShortInfoWithoutSharedSkill,
   ])
   public static canTransferBeforeAnswering(transferNumber: string, isFeatureEnabled: boolean, ringingCallInfo: IShortCallInfo): boolean {
      return (
         isFeatureEnabled &&
         !_.isEmpty(transferNumber) &&
         !_.isNil(ringingCallInfo) &&
         !_.isNil(ringingCallInfo.callId) &&
         ringingCallInfo.peerId !== transferNumber
      );
   }

   @Selector([TransferState.canTransferConnected, TransferState.canTransferBeforeAnswering])
   public static canTransfer(canTransferConnected: boolean, canTransferBeforeAnswering: boolean): boolean {
      return canTransferConnected || canTransferBeforeAnswering;
   }

   @Action(TransferCallAction)
   public transferCall(action: TransferCallAction) {
      const nextDialTarget = this.store.selectSnapshot(NextDialTargetState.nextDialTarget);
      const currentCallId = this.store.selectSnapshot(SamwinLinesStateSelector.getActiveCallId);
      const ringingCallId = this.store.selectSnapshot(SamwinLinesStateSelector.getRingingCallId);
      const callId: number = currentCallId ? currentCallId : ringingCallId;

      const destinationLineState = this.store.selectSnapshot(SamwinLinesStateSelector.getDestinationLineState);

      // Initiate a direct (blind) transfer when line B is idle.
      if (destinationLineState === LineStateEnum.IDLE) {
         return this.callService.transferCall({ targetNumber: nextDialTarget?.number, callId: callId, subscriberId: nextDialTarget?.subscriberId });
      }

      // In all other cases end the call by doing a half-blind transfer.
      return this.store.dispatch(new CompleteTransferAction());
   }
}
